/**
 * Accessibility Utilities and Hooks
 * WCAG 2.1 AA Compliance Helpers
 */

import React, { useEffect, useRef, useState, useCallback } from "react";

/**
 * Focus Management Utilities
 */

// Trap focus within a container (useful for modals, dialogs)
export const trapFocus = (container) => {
  const focusableElements = container.querySelectorAll(
    'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])',
  );

  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  const handleTabKey = (e) => {
    if (e.key !== "Tab") return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  };

  container.addEventListener("keydown", handleTabKey);

  return () => {
    container.removeEventListener("keydown", handleTabKey);
  };
};

// Restore focus to previous element
export const restoreFocus = (previousElement) => {
  if (previousElement && typeof previousElement.focus === "function") {
    previousElement.focus();
  }
};

// Skip to main content link handler
export const skipToMain = (mainId = "main-content") => {
  const mainElement = document.getElementById(mainId);
  if (mainElement) {
    mainElement.tabIndex = -1;
    mainElement.focus();
    mainElement.scrollIntoView();
  }
};

// Scroll to an in-page section and move focus to it, so keyboard and screen
// reader users land where the viewport does (WCAG 2.4.3). Honors
// prefers-reduced-motion instead of unconditionally animating the scroll.
export const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId);
  if (!section) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  section.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
  if (!section.hasAttribute("tabindex")) {
    section.setAttribute("tabindex", "-1");
  }
  section.focus({ preventScroll: true });
};

/**
 * ARIA Live Region Announcer
 */
class AriaAnnouncer {
  constructor() {
    this.liveRegion = null;
    this.init();
  }

  init() {
    if (typeof document === "undefined") return;

    this.liveRegion = document.createElement("div");
    this.liveRegion.setAttribute("role", "status");
    this.liveRegion.setAttribute("aria-live", "polite");
    this.liveRegion.setAttribute("aria-atomic", "true");
    this.liveRegion.className = "sr-only";
    document.body.appendChild(this.liveRegion);
  }

  announce(message, priority = "polite") {
    if (!this.liveRegion) return;

    this.liveRegion.setAttribute("aria-live", priority);
    this.liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = "";
      }
    }, 1000);
  }

  destroy() {
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
  }
}

export const announcer = new AriaAnnouncer();

/**
 * React Hooks for Accessibility
 */

// Hook for managing focus trap
export const useFocusTrap = (isActive = true) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const cleanup = trapFocus(containerRef.current);
    return cleanup;
  }, [isActive]);

  return containerRef;
};

// Hook for managing focus on mount
export const useFocusOnMount = (shouldFocus = true) => {
  const elementRef = useRef(null);

  useEffect(() => {
    if (shouldFocus && elementRef.current) {
      elementRef.current.focus();
    }
  }, [shouldFocus]);

  return elementRef;
};

// Hook for escape key handler
export const useEscapeKey = (handler, isActive = true) => {
  useEffect(() => {
    if (!isActive) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handler(event);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handler, isActive]);
};

// Hook for aria-live announcements
export const useAnnouncement = () => {
  const announce = useCallback((message, priority = "polite") => {
    announcer.announce(message, priority);
  }, []);

  return announce;
};

// Hook for keyboard navigation
export const useKeyboardNavigation = (items, options = {}) => {
  const {
    orientation = "vertical",
    loop = true,
    onSelect,
    onNavigate,
  } = options;

  const [activeIndex, setActiveIndex] = useState(0);

  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;
      let newIndex = activeIndex;

      switch (key) {
        case "ArrowUp":
        case "ArrowLeft":
          if (orientation === "vertical" && key === "ArrowLeft") return;
          if (orientation === "horizontal" && key === "ArrowUp") return;

          event.preventDefault();
          newIndex = activeIndex - 1;
          if (newIndex < 0) {
            newIndex = loop ? items.length - 1 : 0;
          }
          break;

        case "ArrowDown":
        case "ArrowRight":
          if (orientation === "vertical" && key === "ArrowRight") return;
          if (orientation === "horizontal" && key === "ArrowDown") return;

          event.preventDefault();
          newIndex = activeIndex + 1;
          if (newIndex >= items.length) {
            newIndex = loop ? 0 : items.length - 1;
          }
          break;

        case "Home":
          event.preventDefault();
          newIndex = 0;
          break;

        case "End":
          event.preventDefault();
          newIndex = items.length - 1;
          break;

        case "Enter":
        case " ":
          event.preventDefault();
          if (onSelect) {
            onSelect(items[activeIndex], activeIndex);
          }
          return;

        default:
          return;
      }

      setActiveIndex(newIndex);
      if (onNavigate) {
        onNavigate(items[newIndex], newIndex);
      }
    },
    [activeIndex, items, orientation, loop, onSelect, onNavigate],
  );

  return {
    activeIndex,
    setActiveIndex,
    handleKeyDown,
  };
};

/**
 * Color Contrast Utilities
 */

// Calculate relative luminance
const getLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

// Calculate contrast ratio between two colors
export const getContrastRatio = (color1, color2) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

// Convert hex to RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

// Check if contrast meets WCAG standards
export const meetsWCAGStandard = (ratio, level = "AA", largeText = false) => {
  if (level === "AA") {
    return largeText ? ratio >= 3 : ratio >= 4.5;
  } else if (level === "AAA") {
    return largeText ? ratio >= 4.5 : ratio >= 7;
  }
  return false;
};

/**
 * Screen Reader Utilities
 */

// Create screen reader only text
export const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: "0",
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: "0",
};

// Generate descriptive text for complex UI elements
export const generateAriaLabel = (type, context) => {
  const labels = {
    loading: "Loading content, please wait",
    error: `Error: ${context}`,
    success: `Success: ${context}`,
    warning: `Warning: ${context}`,
    info: `Information: ${context}`,
    navigation: `Navigate to ${context}`,
    sort: `Sort by ${context}`,
    filter: `Filter by ${context}`,
    page: `Page ${context}`,
    selected: `${context} selected`,
    expanded: `${context} expanded`,
    collapsed: `${context} collapsed`,
  };

  return labels[type] || context;
};

/**
 * Keyboard Shortcuts Manager
 */
export class KeyboardShortcutsManager {
  constructor() {
    this.shortcuts = new Map();
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.isEnabled = true;
  }

  register(shortcut, handler, options = {}) {
    const { ctrl = false, alt = false, shift = false, meta = false } = options;
    const key = this.generateKey(shortcut, { ctrl, alt, shift, meta });
    this.shortcuts.set(key, handler);
  }

  unregister(shortcut, options = {}) {
    const key = this.generateKey(shortcut, options);
    this.shortcuts.delete(key);
  }

  generateKey(shortcut, { ctrl, alt, shift, meta }) {
    return `${ctrl ? "ctrl+" : ""}${alt ? "alt+" : ""}${shift ? "shift+" : ""}${meta ? "meta+" : ""}${shortcut.toLowerCase()}`;
  }

  handleKeyDown(event) {
    if (!this.isEnabled) return;

    const key = this.generateKey(event.key, {
      ctrl: event.ctrlKey,
      alt: event.altKey,
      shift: event.shiftKey,
      meta: event.metaKey,
    });

    const handler = this.shortcuts.get(key);
    if (handler) {
      event.preventDefault();
      handler(event);
    }
  }

  enable() {
    this.isEnabled = true;
    document.addEventListener("keydown", this.handleKeyDown);
  }

  disable() {
    this.isEnabled = false;
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  destroy() {
    this.disable();
    this.shortcuts.clear();
  }
}

/**
 * Accessibility Testing Helpers
 */

// Check for common accessibility issues
export const auditAccessibility = (element) => {
  const issues = [];

  // Check images for alt text
  const images = element.querySelectorAll("img");
  images.forEach((img) => {
    if (!img.hasAttribute("alt")) {
      issues.push({
        type: "error",
        element: img,
        message: "Image missing alt attribute",
      });
    }
  });

  // Check form inputs for labels
  const inputs = element.querySelectorAll("input, select, textarea");
  inputs.forEach((input) => {
    const id = input.getAttribute("id");
    if (!id || !element.querySelector(`label[for="${id}"]`)) {
      if (
        !input.getAttribute("aria-label") &&
        !input.getAttribute("aria-labelledby")
      ) {
        issues.push({
          type: "error",
          element: input,
          message: "Form input missing label",
        });
      }
    }
  });

  // Check heading hierarchy
  const headings = element.querySelectorAll("h1, h2, h3, h4, h5, h6");
  let previousLevel = 0;
  headings.forEach((heading) => {
    const level = parseInt(heading.tagName[1]);
    if (previousLevel && level > previousLevel + 1) {
      issues.push({
        type: "warning",
        element: heading,
        message: `Heading level skipped from h${previousLevel} to h${level}`,
      });
    }
    previousLevel = level;
  });

  // Check color contrast (simplified check)
  const textElements = element.querySelectorAll("*");
  textElements.forEach((el) => {
    const style = window.getComputedStyle(el);
    const color = style.color;
    const backgroundColor = style.backgroundColor;

    if (color && backgroundColor && backgroundColor !== "rgba(0, 0, 0, 0)") {
      // This is a simplified check - real implementation would need to handle inherited backgrounds
      // and calculate actual contrast ratios
      issues.push({
        type: "info",
        element: el,
        message: "Color contrast should be verified",
      });
    }
  });

  return issues;
};

/**
 * React Component Accessibility HOC
 */
export const withAccessibility = (Component, options = {}) => {
  const { role, ariaLabel, ariaDescribedBy, focusOnMount = false } = options;

  return React.forwardRef((props, ref) => {
    const elementRef = useRef(null);
    const combinedRef = ref || elementRef;

    useEffect(() => {
      if (focusOnMount && combinedRef.current) {
        combinedRef.current.focus();
      }
    }, []);

    return (
      <Component
        {...props}
        ref={combinedRef}
        role={role}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
      />
    );
  });
};

/**
 * Utility to ensure unique IDs for accessibility
 */
let idCounter = 0;
export const generateUniqueId = (prefix = "wixell") => {
  idCounter += 1;
  return `${prefix}-${idCounter}-${Date.now()}`;
};

/**
 * Export all utilities and hooks
 */
export default {
  // Focus management
  trapFocus,
  restoreFocus,
  skipToMain,

  // Announcements
  announcer,

  // React hooks
  useFocusTrap,
  useFocusOnMount,
  useEscapeKey,
  useAnnouncement,
  useKeyboardNavigation,

  // Color contrast
  getContrastRatio,
  meetsWCAGStandard,

  // Screen reader
  srOnly,
  generateAriaLabel,

  // Keyboard shortcuts
  KeyboardShortcutsManager,

  // Testing
  auditAccessibility,

  // Utilities
  withAccessibility,
  generateUniqueId,
};
